import { Suspense, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom';

import './App.css';

import { KnockGuideProvider, KnockProvider } from '@knocklabs/react';

import { usePolyfills } from 'hooks/usePolyfills';
import { isPotentiallyAuthenticated } from 'services/auth/auth';
import { ENV } from 'services/environment/environment';
import { currentL10n } from 'services/l10n/l10n';
import { MultiPartnerService } from 'services/multiPartner/multiPartner';
import { useInfiniteIntegrations } from 'services/repositories/integrations/integrations';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useCurrentUser } from 'services/repositories/user/user';
import { isNewVersionAvailable } from 'services/version/version';
import { getPreviousPath, popFromHistory, pushToHistory } from 'stores/history/history';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { currentEnvironmentIndicatorColor } from 'support/helpers/generic/generic';
import { isPublicRoute } from 'support/helpers/navigation/navigation';
import { Connectors, PartnerDTO, UserDTO } from 'support/types';
import { GlobalPopup } from 'components/GlobalPopup/GlobalPopup';
import { NotificationList } from 'components/NotificationList/NotificationList';
import { OfflineNotification } from 'components/OfflineNotification/OfflineNotification';
import AnalyticsPageView from 'components/Page/PageView';

import '@knocklabs/react/dist/index.css';

export default function Root() {
  usePolyfills();
  const { pathname } = useLocation();
  const isPrivateRoute = !isPublicRoute(pathname);
  const maybeAuthenticated = useMemo(() => isPotentiallyAuthenticated(), []);
  useIdentify(isPrivateRoute && maybeAuthenticated);
  const { data: currentUser } = useCurrentUser({ enabled: isPrivateRoute && maybeAuthenticated });
  const { data: currentPartner } = useCurrentPartner({ enabled: isPrivateRoute && maybeAuthenticated });
  MultiPartnerService.usePartnerSwitchSync(currentPartner?.id);

  // On every route change:
  // - Reload the page if a new version is available
  // - Add the current route to the navigation history or pop the last route if the current route is the previous one
  useEffect(() => {
    const isGoingBack = getPreviousPath() === pathname;
    if (isGoingBack) {
      popFromHistory();
    } else {
      pushToHistory(pathname);
    }
    if (isNewVersionAvailable) {
      window.location.reload();
    }
  }, [pathname]);

  return (
    <Suspense fallback="...loading">
      <Helmet>
        <html lang={currentL10n} />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={`/assets/favicons/${ENV.BACKEND_ENV}/apple-touch-icon.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`/assets/favicons/${ENV.BACKEND_ENV}/favicon-32x32.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`/assets/favicons/${ENV.BACKEND_ENV}/favicon-16x16.png`}
        />
        <link rel="manifest" href={`/assets/favicons/${ENV.BACKEND_ENV}/site.webmanifest`} />
        <link
          rel="mask-icon"
          href={`/assets/favicons/${ENV.BACKEND_ENV}/safari-pinned-tab.svg`}
          color={currentEnvironmentIndicatorColor}
        />
        <link rel="shortcut icon" href={`/assets/favicons/${ENV.BACKEND_ENV}/favicon.ico`} />
        <meta name="msapplication-TileColor" content={currentEnvironmentIndicatorColor} />
        <meta name="msapplication-config" content={`/assets/favicons/${ENV.BACKEND_ENV}/browserconfig.xml`} />
        <meta name="theme-color" content="#ffffff" />
      </Helmet>
      <AnalyticsPageView />
      <ScrollRestoration />
      <NotificationList />
      <GlobalPopup />
      <OfflineNotification />
      <KnockProvider apiKey={ENV.KNOCK_API_KEY} user={currentUser ?? { id: 'anonymous_user' }}>
        <KnockGuideProvider
          readyToTarget={!!currentUser && !!currentPartner}
          channelId={ENV.KNOCK_GUIDE_CHANNEL_ID}
          targetParams={{
            data: {
              language: currentUser?.language ?? 'en',
              partnerType: currentPartner?.partnerType ?? null,
            },
          }}
        >
          <Outlet />
        </KnockGuideProvider>
      </KnockProvider>
    </Suspense>
  );
}

const useIdentify = (enabled: boolean) => {
  const analytics = useAnalytics();
  const { data: currentUser, isLoading: isLoadingUser, isError: isErrorUser } = useCurrentUser({ enabled });
  const { data: currentPartner, isLoading: isLoadingPartner, isError: isErrorPartner } = useCurrentPartner({ enabled });
  useIdentifyConnectors(currentUser, currentPartner);

  useEffect(() => {
    // Wait until both queries have settled — do not act while still loading
    if (isLoadingUser || isLoadingPartner) {
      return;
    }

    if (!currentUser || !currentPartner || isErrorUser || isErrorPartner) {
      analytics.reset();
      return;
    }

    analytics.identify(currentUser, currentPartner);
  }, [analytics, currentUser, currentPartner, isLoadingUser, isLoadingPartner, isErrorUser, isErrorPartner]);
};

const useIdentifyConnectors = (user: UserDTO | undefined, partner: PartnerDTO | undefined) => {
  const analytics = useAnalytics();
  const { data: integrations, isLoading: isLoadingIntegrations } = useInfiniteIntegrations({
    enabled: Boolean(user && partner),
  });

  useEffect(() => {
    if (!user || !partner || isLoadingIntegrations) {
      return;
    }

    const integrationConnectors =
      integrations?.pages.flatMap((page) => page.items)?.map((integration) => integration.connector) ?? [];
    const hasWebEDIIntegration = integrationConnectors.includes(Connectors.WEB_EDI);

    analytics.track('$set', {
      $set: { hasWebEDIIntegration, connectors: integrationConnectors },
    });

    analytics.track('$groupidentify', {
      $group_type: 'tradePartner',
      $group_key: partner.id,
      $group_set: {
        hasWebEDIIntegration,
        connectors: integrationConnectors,
      },
    });
  }, [analytics, integrations?.pages, isLoadingIntegrations, user, partner]);
};
